import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output
} from '@angular/core';
import {Subscription} from 'rxjs';
import {environment} from '../../environments/environment';
import {DataService} from '../services/data.service';
import {isNullOrUndefined} from '../shared/is-null-or-undefined';
import {PermissionService} from '../services/permission.service';
import {AdminPermissions} from '../models/admin-permissions';

@Component({
    selector: 'app-sidebar',
    templateUrl: './sidebar.component.html',
    styleUrls: ['./sidebar.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SidebarComponent implements OnInit, OnDestroy {
    kioskUnreachable = false;
    private kioskTimer: ReturnType<typeof setInterval>;
    private sessionSubscription: Subscription;
    private kioskRequest: AbortController | null = null;
    private destroyed = false;
    readonly permissions = AdminPermissions;
    @Input() isOpen = false;
    @Output() navigationSelected = new EventEmitter<void>();

    isDispatcherUser = false;
    isAdminUser = false;
    isDispatcherManager = false;

    constructor(private dataService: DataService,
                public permissionService: PermissionService,
                private changeDetectorRef: ChangeDetectorRef) {
    }

    ngOnInit() {
        this.checkIfDispatcherUser();
        this.sessionSubscription = this.dataService.dataChangedSession.subscribe(() => {

            this.checkIfDispatcherUser();
            this.kioskUnreachable = false;
            this.kioskRequest?.abort();
        });
        void this.checkKiosk();
        this.kioskTimer = setInterval(() => void this.checkKiosk(), 10000);
    }

    ngOnDestroy(): void {
        this.destroyed = true;
        clearInterval(this.kioskTimer);
        this.sessionSubscription?.unsubscribe();
        this.kioskRequest?.abort();
    }

    private async checkKiosk(): Promise<void> {
        const token = this.dataService.getSessionIdentifier();
        if (this.kioskRequest || !token || !this.isAdminUser ||
            !this.permissionService.hasAny([this.permissions.ViewKioskLogs, this.permissions.RestartKiosk])) return;

        const controller = new AbortController();
        this.kioskRequest = controller;
        const timeout = setTimeout(() => controller.abort(), 8000);
        try {
            const response = await fetch(`${environment.baseUrl}/soc-admin/airport-kiosk/status`, {
                headers: {Token: token}, signal: controller.signal
            });
            if (response.status === 401 || response.status === 403) return;
            if (!response.ok) throw new Error('Kiosk status unavailable');
            const result = await response.json() as { Success: boolean };
            if (!this.destroyed && token === this.dataService.getSessionIdentifier()) {
                this.kioskUnreachable = result.Success === false;
            }
        } catch {
            if (!this.destroyed && token === this.dataService.getSessionIdentifier()) this.kioskUnreachable = true;
        } finally {
            clearTimeout(timeout);
            this.kioskRequest = null;
            if (!this.destroyed) this.changeDetectorRef.markForCheck();
        }
    }

    checkIfDispatcherUser() {
        this.isDispatcherUser = false;
        this.isDispatcherManager = false;
        this.isAdminUser = false;

        const userData = this.dataService.getUserInfo();
        if (!isNullOrUndefined(userData) && !isNullOrUndefined(userData.Permissions)) {
            const permissions = userData.Permissions.map(permission => String(permission).trim().toLowerCase());
            if (permissions.includes('isdispatcheruser')) {
                this.isDispatcherUser = true;
            } else if (permissions.includes('isdispatchermanager')) {
                this.isDispatcherManager = true;
            } else {
                this.isAdminUser = true;
            }
        }
    }

    handleNavigationClick() {
        this.navigationSelected.emit();
    }
}
